<template>
  <div class="pricing-plan__card-wrapper">
    <div v-if="showMostPopular" class="pricing-plan__most-popular">
      {{ $t('fields.pricing_table.plan_popular') }}
    </div>

    <v-card
      outlined
      height="100%"
      class="pricing-plan__card"
      :class="[
        {
          'pricing-plan__card--most-popular': showMostPopular,
        },
      ]"
    >
      <v-card-text class="pb-2 pt-sm-0 h-100">
        <v-row>
          <v-col cols="12" sm="6" md="12" lg="12">
            <div class="pricing-plan__title-wrapper">
              <!-- Plan title -->
              <v-card-title class="pricing-plan__title">
                <h3 class="text-h3 lapis--text">
                  {{ $t(`fields.${plan.translationKey}`) }}
                </h3>
              </v-card-title>
              <!-- Plan title END -->

              <!-- Plan subtitle -->
              <v-card-subtitle class="pricing-plan__subtitle">
                <p class="subtitle-2">
                  {{ $t(plan.subtitle) }}
                </p>
              </v-card-subtitle>
              <!-- Plan subtitle END -->
            </div>

            <!-- Plan price -->
            <v-row>
              <v-col xs="12" class="pricing-plan__price-wrapper">
                <span class="pricing-plan__currency">
                  {{ returnSymbol }}
                </span>
                <span class="pricing-plan__price">
                  {{ returnDisplayPrice }}
                </span>
                <span :class="plan.strikethrough_price ? 'pricing-plan__strikethrough_price-container' : ''">
                  <span v-if="plan.strikethrough_price" class="pricing-plan__strikethrough_price">
                    {{ returnSymbol }} {{ returnStrikethroughPriceMonthly }}
                  </span>
                  <span class="pricing-plan__billing"> /{{ $t('fields.pricing_table.plan_price_per_month') }} </span>
                </span>
              </v-col>
            </v-row>
            <!-- Plan price END -->

            <!-- Plan billing -->
            <v-row>
              <v-col xs="12" class="pricing-plan__billing-info">
                {{
                  planToDisplay.interval === 'month'
                    ? $t('fields.pricing_table.monthly_charge', { amount: totalDisplayPrice })
                    : $t('fields.pricing_table.annual_charge', { amount: totalDisplayPrice })
                }}

                <div class="mt-6 billing-interval-selection__wrapper">
                  <div v-if="monthlyPlanVariant" class="billing-interval-selection mt-6">
                    <v-switch
                      v-model="isAnnual"
                      class="pa-0 ma-0 ml-3 interval-switch"
                      hide-details
                      inset
                      :ripple="false"
                      color="#0474CD"
                      :dense="true"
                    ></v-switch>
                    <span :class="{ active: isAnnual }" class="billing-interval-selection__label">
                      <span class="billing-interval-selection__label-text" @click="isAnnual = !isAnnual">
                        {{ $t('fields.pricing_table.billing_interval_switch.label') }}
                      </span>
                      <div v-if="isAnnual && savingsPercent > 0" class="save-badge">
                        {{
                          $t('fields.pricing_table.billing_interval_switch.badge_text', {
                            amount: savingsPercent,
                          })
                        }}
                      </div>
                    </span>
                  </div>
                </div>
              </v-col>
            </v-row>
            <!-- Plan billing END -->

            <!-- Select button desktop-->
            <v-row align="center" justify="center" class="pricing-plan__select-btn--desktop">
              <v-col xs="12" md="8" lg="8" align="center" justify="center" class="pt-4 pb-0">
                <v-btn
                  depressed
                  rounded
                  :ripple="false"
                  large
                  :outlined="subscribeBtnState === 'downgrade'"
                  :color="subscribeBtnState === 'downgrade' ? 'neutral darken-3' : 'primary'"
                  :loading="loading"
                  :disabled="subscribeBtnState === 'selected' || loading"
                  :data-test="'plan_' + planToDisplay.planName + '_' + subscribeBtnState"
                  @click="planSelected(plan, subscribeBtnState)"
                >
                  <span> {{ subscribeBtnLabel }} </span>
                </v-btn>
              </v-col>
            </v-row>
            <!-- Select button desktop END -->
          </v-col>

          <v-col cols="12" sm="6" :md="12" lg="12" class="px-4">
            <!-- Plan features -->
            <v-simple-table class="pricing-plan__feature-table">
              <template #default>
                <tbody>
                  <template v-for="feature in featureList">
                    <tr v-if="feature.value !== null && feature.value !== '0'" :key="feature.title">
                      <td class="text-center pricing-plan__feature-amount-wrapper">
                        <div class="pricing-plan__feature-amount">
                          <template v-if="feature.value === '-1'">
                            {{ $t('fields.pricing_table.feature_list.unlimited_value').toString() }}
                          </template>
                          <template v-else-if="feature.title === 'Scans' && isUserOnAbtestVariantCNV3020()">
                            Unlimited
                          </template>
                          <template v-else>
                            {{ feature.value }}
                          </template>
                        </div>
                      </td>
                      <td>
                        <v-tooltip top transition="none" max-width="320">
                          <template #activator="{ on }">
                            <v-btn
                              text
                              :ripple="false"
                              color="primary"
                              small
                              class="v-btn--text--link--dashed"
                              v-on="on"
                            >
                              <span>
                                {{ feature.title }}
                              </span>
                            </v-btn>
                          </template>
                          <span v-html="feature.tooltip" />
                        </v-tooltip>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
            <!-- Plan features END -->
          </v-col>
        </v-row>

        <!-- Plan support -->
        <template v-if="plan.hasPremiumSupport">
          <v-row>
            <v-col class="text-center pa-0 mt-7 mt-sm-0">
              <hr class="pricing-plan__fullwidth-border" />
              <span class="pricing-plan__premium-support">
                {{ $t('fields.pricing_table.premium_support') }}
              </span>
            </v-col>
          </v-row>
        </template>
        <!-- Plan support END -->

        <v-row align="center" justify="center" class="pricing-plan__select-btn--mobile">
          <hr class="pricing-plan__fullwidth-border pricing-plan__fullwidth-border--mobile" />
          <v-col cols="12" align="center" justify="center" class="pt-4 pb-2">
            <v-btn
              :id="plan.id"
              depressed
              rounded
              :ripple="false"
              large
              :outlined="subscribeBtnState === 'downgrade'"
              :color="subscribeBtnState === 'downgrade' ? 'neutral darken-3' : 'primary'"
              :loading="loading"
              :disabled="subscribeBtnState === 'selected' || loading"
              :data-test="'plan_' + planToDisplay.planName + '_' + subscribeBtnState"
              @click="planSelected(plan, subscribeBtnState)"
            >
              <span>
                {{ subscribeBtnLabel }}
              </span>
            </v-btn>
          </v-col>
        </v-row>
        <!-- Select button desktop END -->
      </v-card-text>
    </v-card>
    <!-- Reactivate subscription -->
    <reactivate-subscription
      :open="reactivateModalShow"
      :renew="activeSubscription.current_period_end.format('MMM DD, YYYY')"
      @reactivate="onReactivate"
      @cancel="onCancel"
    />
  </div>
</template>

<script lang="ts">
import Currency from '@/Models/PrimitiveModels/Currency/Currency';
import { trackCheckoutNowButtonPricingPageStep1 } from '@/Services/GoogleTagManager';
import { trackCheckoutNowButtonPricingPage } from '@/Services/Snowplow/SnowplowService';
import { annualSavingsPercent, labelForPlanState } from '@/Services/Subscription/PlanViewService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import { Team } from '@/classes/auth';
import { Plan, Subscription } from '@/classes/stripe';
import ReactivateSubscription from '@/components/Modals/Account/ReactivateSubscription.vue';
import store from '@/store';
import { AccountState } from '@/store/account';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import OptimizelyService from '@/Services/OptimizelyService';

@Component({
  components: {
    ReactivateSubscription,
  },
  computed: {
    ...mapState<AccountState>('account', {
      id: (state: AccountState) => state.account?.id || [],
      qrcodes: (state: AccountState) => state.account?.qrcodes || [],
    }),
    ...mapGetters('auth', {
      team: 'team',
    }),
  },
})
export default class PlanTile extends Vue {
  @Prop({ default: () => new Plan() })
  plan!: Plan;

  @Prop({ default: undefined })
  monthlyPlanVariant!: Plan | undefined;

  @Prop({ default: () => new StripeSubscriptionService() })
  stripeSubscriptionService!: StripeSubscriptionService;

  @Prop({ default: 'month' })
  selectedInterval!: string;

  @Prop({ default: false })
  loading!: boolean;

  get activeSubscription(): Subscription {
    return store.state.subscription.activeSubscription;
  }

  get planToDisplay(): Plan {
    if (this.monthlyPlanVariant && !this.isAnnual) {
      return this.monthlyPlanVariant;
    }

    return this.plan;
  }

  // Savings of the annual plan over this tier's monthly plan; the badge is hidden when there are none.
  get savingsPercent(): number {
    return this.monthlyPlanVariant ? annualSavingsPercent(this.plan, this.monthlyPlanVariant) : 0;
  }

  team!: Team;

  qrcodes: any;

  reactivateModalShow = false;
  isAnnual: boolean = true;

  get isSubscribedToMonthlyVariant(): boolean {
    return !!this.monthlyPlanVariant && this.activeSubscription?.plan_id === this.monthlyPlanVariant.id;
  }

  // A customer on this tier's monthly plan sees the tile on monthly billing, so their active plan is shown.
  // Watched because the subscription loads after the tiles are rendered.
  @Watch('isSubscribedToMonthlyVariant', { immediate: true })
  onSubscribedToMonthlyVariant(isSubscribed: boolean): void {
    if (isSubscribed) {
      this.isAnnual = false;
    }
  }

  // Limits of this plan as a clean list, null values won't be shown
  get featureList(): Array<{ title: string; value: string | null; tooltip: string }> {
    return [
      {
        title: this.$t('fields.pricing_table.feature_list.dynamic_codes_title').toString(),
        value: this.plan.codeLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.dynamic_codes_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.scans_title').toString(),
        value: this.plan.scanLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.scans_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.bulk_title').toString(),
        value: this.plan.bulkLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.bulk_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.api_requests_title').toString(),
        value: this.plan.apiLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.api_requests_description').toString(),
      },
      {
        title: this.$t('fields.pricing_table.feature_list.users_title').toString(),
        value: this.plan.userLimit,
        tooltip: this.$t('fields.pricing_table.feature_list.users_description').toString(),
      },
    ];
  }

  get totalDisplayPrice(): string {
    const totalPrice = this.planToDisplay.price.priceToInt();
    const priceWithCurrency = new Currency(totalPrice);

    return `${this.returnSymbol} ${priceWithCurrency.priceAsValue({
      price: totalPrice,
    })}`;
  }

  get subscribeBtnLabel(): string {
    return this.subscribeButtonLabel().subscribeBtnLabel;
  }

  get subscribeBtnState(): string {
    return this.subscribeButtonLabel().subscribeBtnState;
  }

  get returnDisplayPrice(): string {
    const displayOriginalPrice = new Currency(this.planToDisplay.price.price);

    return displayOriginalPrice.displayPriceMonthly({
      plan: this.planToDisplay,
      selectedInterval: this.selectedInterval,
    });
  }

  get showExcludedVatTaxTooltip(): boolean {
    const currenciesWithVatTooltip = ['$', '€', '£'];
    return currenciesWithVatTooltip.includes(this.returnSymbol);
  }

  get returnExcludedVatTaxLabel(): string {
    return this.$t(
      `fields.pricing_table.excluded_vat_tooltip.${this.returnSymbol === '$' ? 'excluded_tax' : 'excluded_vat'}`
    ).toString();
  }

  get returnYearlyCostWithCurrency(): string {
    const price = this.plan.price.price;
    switch (this.returnSymbol) {
      case '$':
      case 'R$':
      case '£': {
        return `${this.returnSymbol} ${price}`;
      }
      default:
        return `${price} ${this.returnSymbol}`;
    }
  }

  get returnSymbol(): string {
    return this.planToDisplay.price.displaySymbol({ overrideCurrency: this.planToDisplay.currency });
  }

  get returnStrikethroughPriceMonthly(): string | undefined {
    if (this.plan.strikethrough_price) {
      return (parseFloat(this.plan.strikethrough_price) / 12).toFixed(2);
    }
    return undefined;
  }

  /**
   * Return the original price of a plan.
   */
  get originalPlanPrice(): number | undefined {
    return this.plan?.originalPriceMonthly?.price || undefined;
  }

  get showMostPopular(): boolean | undefined {
    return this.plan.popular;
  }

  /**
   * Sets the button label according to which plan the user currently has
   */
  subscribeButtonLabel(): { subscribeBtnLabel: string; subscribeBtnState: string } {
    const planState = this.stripeSubscriptionService.planState(this.planToDisplay.id);
    const { label, state } = labelForPlanState(planState);

    return { subscribeBtnLabel: String(this.$t(label)), subscribeBtnState: state };
  }

  planSelected(plan: Plan, state: string | 'continue' | 'reactivate'): void {
    if (state === 'continue') {
      const language = store.state?.auth?.user?.language;
      const user_id = store.state?.auth?.user?.id;
      const account = store.state?.account?.account;
      const price_currency_displayed = store.state?.auth?.team?.currency;
      const exceedsCodeLimit = !this.stripeSubscriptionService.canDowngradePlan(this.planToDisplay.id);

      if (exceedsCodeLimit) {
        this.$emit('purchase-blocked-because-qr-quota', new Plan(this.planToDisplay));
        return;
      }

      trackCheckoutNowButtonPricingPage({
        industry_id: account?.industry_id ?? null,
        user_id,
        price_currency_displayed,
        language_code: language,
        plan_id: this.planToDisplay.id,
      });
      trackCheckoutNowButtonPricingPageStep1(
        window.dataLayer,
        this.planToDisplay.planName,
        this.planToDisplay.price.price
      );
    }

    if (state === 'reactivate') {
      this.reactivateModalShow = true;
    } else if (state === 'upgrade' || state === 'downgrade') {
      this.notifyParentToOpenModal(state);
    } else {
      // choose selected plan
      this.$store.dispatch('subscription/setSubscriptionPlan', this.planToDisplay).then(() => {
        this.$router.push({ name: 'checkout_payment_form', params: { planId: this.planToDisplay.id } });
      });
    }
  }

  notifyParentToOpenModal(intent: 'upgrade' | 'downgrade'): void {
    const planClone = new Plan(this.planToDisplay);
    this.$emit('subscription-change-request', { plan: planClone, intent });
  }

  async onReactivate(): Promise<void> {
    await this.stripeSubscriptionService.initSubscription();
    this.onCancel();
  }

  onCancel(): void {
    this.reactivateModalShow = false;
  }

  isUserOnAbtestVariantCNV3020(): boolean {
    return OptimizelyService.isUserOnAbtestVariantCNV3020();
  }
}
</script>

<style lang="scss">
.pricing-plan {
  &__card {
    border-width: 2px !important;

    &-wrapper {
      height: 100%;
      position: relative;

      @media (max-width: 959px) {
        padding-top: 24px;
      }

      &--disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    }
  }

  &__card--most-popular {
    margin-top: -30px;
    border-color: map-get($turquoise, 'darken-1') !important;
    border-top-right-radius: 0 !important;
    border-top-left-radius: 0 !important;
  }

  &__most-popular {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    top: -28px;
    background: map-get($turquoise, 'darken-1');
    border: 2px solid map-get($turquoise, 'darken-1');
    text-align: center;
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
    height: 32px;
    font-size: 12px;
    border-top-right-radius: 12px !important;
    border-top-left-radius: 12px !important;
    color: map-get($neutral, 'lighten-4');
  }

  &__title {
    text-align: center;
    justify-content: center;

    @media (min-width: 600px) and (max-width: 959px) {
      padding-top: 0;
    }

    &--flexplan {
      padding: 0 16px 10px;
    }
  }

  &__subtitle {
    text-align: center;
    padding-bottom: 0;

    .subtitle-2 {
      color: map-get($lapis, 'lighten-2');
      font-weight: map-get($font-weights, 'regular');
    }
  }

  &__price-wrapper {
    text-align: center;
    padding-bottom: 0;
  }

  &__billing {
    position: relative;
    font-size: 12px;
    color: map-get($lapis, 'lighten-2');
    font-weight: map-get($font-weights, 'regular');
  }

  &__currency {
    position: relative;
    font-size: 18px;
    color: map-get($lapis, 'base');
    font-weight: map-get($font-weights, 'bold');
  }

  &__billing-info {
    padding: 0 12px 8px;
    text-align: center;
    color: map-get($lapis, 'lighten-2');
  }

  &__price {
    font-size: 40px;
    color: map-get($lapis, 'base');
    font-weight: map-get($font-weights, 'black');
  }

  &__price-increased {
    font-size: 12px;
    color: map-get($begonia, 'lighten-1');
    font-weight: map-get($font-weights, 'regular');
    text-decoration: line-through;
    position: absolute;
  }

  &__strikethrough_price-container {
    display: inline-flex;
    flex-direction: column;
    top: -24px;
    position: relative;
    margin-bottom: -24px;
  }

  &__strikethrough_price {
    position: relative;
    right: 5px;
    bottom: -12px;
    font-size: 12px;
    color: map-get($begonia, 'lighten-1');
    text-decoration: line-through;
    font-weight: map-get($font-weights, 'bold');
  }

  &__currency {
    top: -22px;
    left: 2px;
  }

  &__billing {
    top: 4px;
    right: 8px;
  }

  &__feature-table {
    tr:hover {
      background: transparent !important;
    }

    td {
      border-bottom: none !important;
      height: 32px !important;
      padding: 0 8px !important;
    }

    .v-data-table__wrapper {
      overflow-x: hidden;
    }
  }

  &__feature {
    &-amount {
      background: map-get($neutral, 'lighten-2');
      border-radius: 4px;
      color: map-get($lapis, 'lighten-1');
      height: 24px;
      width: 80px;

      &-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
    }
  }

  &__fullwidth-border {
    border-top: 1px solid #e6edf0;
    width: calc(100% + 9px);
    margin-bottom: 8px;
    margin-left: -5px;

    @media (min-width: 600px) and (max-width: 959px) {
      margin-bottom: 4px;
    }

    &--mobile {
      display: none;

      @media (min-width: 600px) and (max-width: 959px) {
        display: block;
        margin-top: 4px;
        margin-bottom: 0;
      }
    }
  }

  &__premium-support {
    color: map-get($lapis, 'lighten-2');
    font-weight: map-get($font-weights, 'semi-bold');
    font-size: 18px;
  }

  &__select-btn--desktop {
    @media (min-width: 600px) and (max-width: 959px) {
      display: none;
    }
  }

  &__select-btn--mobile {
    display: none;

    @media (min-width: 600px) and (max-width: 959px) {
      display: flex;
    }
  }

  &__flex-header {
    font-weight: 600;
    font-size: 14px;
    text-align: center;
    color: #1b294b;
    padding: 4px 0 6px 0;
    margin-bottom: -14px;
    @media (min-width: 600px) and (max-width: 959px) {
      margin-bottom: 0;
    }
  }
}

.billing-interval-selection {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  .interval-switch {
    .v-input--selection-controls__input {
      margin-right: 4px;
    }
  }

  &__wrapper {
    min-height: 24px;
  }

  &__label {
    position: relative;

    &-text {
      font-size: 16px;
      cursor: pointer;
    }

    &.active {
      color: map-get($lapis, 'lighten-1');
    }
    color: map-get($neutral, 'darken-2');
  }

  .save-badge {
    background-color: #b1f0d5;
    color: map-get($lapis, 'base');
    font-weight: 700;
    font-size: 12px;
    height: 20px;
    padding: 0 6px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 8px;
    white-space: nowrap;
  }
}
</style>
